import { Trans, useLingui } from '@lingui/react/macro'
import loginScreenLogo from '$src/assets/images/login_screen_logo.png'
import { authHooks } from '$src/lib/trpcHooks'
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Divider,
  Image,
  Paper,
  rem,
  Stack,
  Text,
  Title,
} from '@mantine/core'

import { IconArrowRight } from '@tabler/icons-react'
import { useState } from 'react'
import { GoogleButton } from './GoogleButton'

interface WelcomeStepProps {
  onContinue: () => void
}

export function WelcomeStep({ onContinue }: WelcomeStepProps) {
  const { t } = useLingui()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [showTermsError, setShowTermsError] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)

  const { refetch: getGoogleUrl } = authHooks.useGoogleLogin()

  const handleGetStarted = () => {
    if (!termsAccepted) {
      setShowTermsError(true)
      return
    }
    setShowTermsError(false)
    onContinue()
  }

  const handleGoogleLogin = async () => {
    if (!termsAccepted) {
      setShowTermsError(true)
      return
    }
    setShowTermsError(false)
    setIsGoogleLoading(true)

    const { data } = await getGoogleUrl()
    if (data?.url) {
      window.location.href = data.url
    } else {
      setIsGoogleLoading(false)
    }
  }

  return (
    <>
      <Paper
        radius="xl"
        p="xl"
        withBorder
        shadow="xl"
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          width: '100%',
        }}
      >
        <Stack align="center" gap="xl">
          <Image
            src={loginScreenLogo}
            alt="Markku"
            style={{ maxWidth: '280px', width: '100%' }}
          />

          <Stack align="center" gap="xs">
            <Title order={2} ta="center" c="brandBrown.7">
              <Trans>Welcome to Markku!</Trans>
            </Title>
            <Text size="md" c="dimmed" ta="center">
              <Trans>
                A maintenance book that takes care of your home. Track
                maintenance needs, receive reminders and maintain your
                property's value.
              </Trans>
            </Text>
          </Stack>

          {showTermsError && (
            <Alert color="red" title={t`Accept terms of service`}>
              <Trans>You must accept the terms of service to continue.</Trans>
            </Alert>
          )}

          <Checkbox
            size="md"
            checked={termsAccepted}
            onChange={(event) => setTermsAccepted(event.currentTarget.checked)}
            label={
              <Text size="md">
                <Trans>
                  I accept the{' '}
                  <Anchor
                    href="https://markku.fi/kayttoehdot/"
                    target="_blank"
                    size="md"
                  >
                    terms of service
                  </Anchor>{' '}
                  and{' '}
                  <Anchor
                    href="https://markku.fi/tietosuoja/"
                    target="_blank"
                    size="md"
                  >
                    privacy policy
                  </Anchor>
                </Trans>
              </Text>
            }
          />

          <Button
            fullWidth
            size="lg"
            radius="xl"
            color="brandOrange"
            rightSection={<IconArrowRight size={20} />}
            onClick={handleGetStarted}
            style={{
              height: rem(56),
              fontSize: rem(18),
            }}
          >
            <Trans>Get started</Trans>
          </Button>

          <Divider
            label={t`or`}
            labelPosition="center"
            style={{ width: '100%' }}
          />

          <GoogleButton
            onClick={handleGoogleLogin}
            loading={isGoogleLoading}
            fullWidth
          />
        </Stack>
      </Paper>
    </>
  )
}
